import { Injectable } from "@angular/core";
import * as LaunchDarkly from "launchdarkly-js-client-sdk";
import { LDFlagSet, LDFlagValue } from "launchdarkly-js-client-sdk";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { UserService } from '../../core/services/user.service';
import { User } from '../../core/models/user.model';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { FeatureFlagsEnum } from '../enums';

@Injectable({
  providedIn: "root",
})
export class FeatureFlagService {
  private client: LaunchDarkly.LDClient;
  private context: LaunchDarkly.LDContext;
  private readonly flagsSubject: BehaviorSubject<LDFlagSet> = new BehaviorSubject<LDFlagSet>(null);

  constructor(
    private userService: UserService,
  ) {}

  initialize(): void {
    this.userService.getUserData()
      .pipe(
        debounceTime(200),
        filter((user: User) => {
          if (!user && this.client) {
            this.flagsSubject.next(null);
            this.client.close();
            return false;
          }
          return Boolean(user);
        })
      )
      .subscribe((user: User) => {
        this.context = this.getUserInfo(user);

        this.client = LaunchDarkly.initialize(
          environment.launchDarklyClientId,
          this.context,
        );

        this.client.waitUntilReady().then(() => {
          const flags: LDFlagSet = this.client.allFlags();
          this.flagsSubject.next(flags);
        });

        this.client.on('change', (changedFlags: LDFlagSet) => {
          const existingFlags = this.flagsSubject.getValue();
          const updatedFlags = Object.keys(changedFlags).reduce((acc: LDFlagSet, key: string) => ({
            ...acc,
            [key]: changedFlags[key].current
          }), existingFlags);
          this.flagsSubject.next(updatedFlags);
        });
      });
  }

  getFlag(flagKey: FeatureFlagsEnum | string, defaultValue: LDFlagValue = false): Observable<LDFlagValue> {
    return this.flagsSubject.asObservable().pipe(
        map(() => this.getCurrentFlag(flagKey, defaultValue)),
        distinctUntilChanged(),
    );
  }

  /**
   *
   * Use only to get current state in current period of time, in other cases use {@link getFlag} observable method
   *
   * */
  getCurrentFlag(flagKey: FeatureFlagsEnum | string, defaultValue: LDFlagValue = false): LDFlagValue {
    const flags: LDFlagSet = this.flagsSubject.getValue();
    if (flags) {
      return flags.hasOwnProperty(flagKey) ? flags[flagKey] : defaultValue;
    }
    return defaultValue;
  }

  areFlagsInitialized(): Observable<boolean> {
    return this.flagsSubject.asObservable().pipe(
      map((flags: LDFlagSet): boolean => flags !== null),
      distinctUntilChanged(),
    );
  }

  private getUserInfo(user: User) {
    const account_creation_date: Date = new Date(user.account_creation_date);
    const creation_date = new Date(user.creation_date);
    const billing_period_start = new Date(user.billing_period_start);
    const billing_period_end = new Date(user.billing_period_end);
    const context: LaunchDarkly.LDContext = {
      kind: 'multi',
      user: {
        key: 'user',
        user_id: user.id,
        email: user.email,
        created_at: creation_date.getTime() / 1000,
        is_admin: user.is_admin,
        is_ads_intel_user: user.is_ads_intel_user,
        is_autorenew: user.is_autorenew,
        is_chrome_only: user.is_chrome_only,
        is_prospectconnect_user: user.is_prospect_connect_user,
        name: [user.first_name, user.last_name].join(' '),
        revdriver_version: user.rev_driver_version || null,
      },
      company: {
        key: 'company',
        accounttype: user.account_type?.toString(),
        account_creation_date: account_creation_date.getTime() / 1000,
        created_at: account_creation_date.getTime() / 1000,
        adsintel: user.ads_intel_enabled,
        billing_start_at: billing_period_start.getTime() / 1000,
        billing_end_at: billing_period_end.getTime() / 1000,
        company_id: user.account_id.toString(),
        job_change_enabled: user.job_change_enabled,
        job_change_freemium: user.job_change_freemium,
        name: user.account_name,
        prospectconnect: user.prospect_connect_enabled,
        user_limit: user.user_limit,
      }
    }
    return context;
  }
}
